/**
 * Server functions for browser push (web push) opt-in.
 *
 * Public surface is deliberately tiny:
 *   • getPushPublicKey     — hands the browser the VAPID public key
 *   • savePushSubscription — stores/updates one browser subscription
 *   • removePushSubscription — revokes on unsubscribe / permission loss
 *
 * Subscriptions are written with the service-role client because the
 * table is admin-read-only by RLS: a visitor may create their own row but
 * must never be able to read anyone else's push address.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SubscriptionSchema = z
  .object({
    provider: z.enum(["webpush", "fcm"]).default("webpush"),
    endpoint: z.string().min(5).max(1000),
    fcmToken: z.string().min(20).max(4096).optional(),
    p256dh: z.string().min(10).max(255).optional(),
    auth: z.string().min(4).max(255).optional(),
    anonymousId: z.string().max(64).optional(),
    promptSource: z.string().max(40).optional(),
    userAgent: z.string().max(400).optional(),
  })
  .refine((v) => (v.provider === "fcm" ? Boolean(v.fcmToken) : Boolean(v.p256dh && v.auth)), {
    message: "missing transport credentials for provider",
  });


export type SavePushSubscriptionInput = z.input<typeof SubscriptionSchema>;

export const getPushPublicKey = createServerFn({ method: "GET" }).handler(async () => {
  return { publicKey: process.env["VAPID_PUBLIC_KEY"] || "" };
});

export const savePushSubscription = createServerFn({ method: "POST" })
  .inputValidator((data: SavePushSubscriptionInput) => SubscriptionSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Best-effort identity attachment: link the browser to the known lead
    // for this anonymous id so "visited once" audiences can be targeted.
    let leadProfileId: string | null = null;
    if (data.anonymousId) {
      const { data: lead } = await supabaseAdmin
        .from("lead_profiles" as never)
        .select("id")
        .eq("anonymous_id" as never, data.anonymousId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      leadProfileId = (lead as unknown as { id: string } | null)?.id ?? null;
    }

    const { error } = await supabaseAdmin
      .from("push_subscriptions" as never)
      .upsert(
        {
          provider: data.provider,
          endpoint: data.endpoint,
          fcm_token: data.provider === "fcm" ? (data.fcmToken ?? null) : null,
          p256dh: data.p256dh ?? null,
          auth: data.auth ?? null,
          anonymous_id: data.anonymousId ?? null,
          lead_profile_id: leadProfileId,
          prompt_source: data.promptSource ?? null,
          user_agent: data.userAgent ?? null,
          revoked_at: null,
        } as never,
        { onConflict: "endpoint" },
      );
    if (error) {
      console.error(`[push] subscription save failed (${data.provider}): ${error.message}`);
      throw new Error(`push subscription save failed: ${error.message}`);
    }
    return { ok: true };
  });

export const removePushSubscription = createServerFn({ method: "POST" })
  .inputValidator((data: { endpoint: string }) =>
    // `fcm:<token>` endpoints are not URLs, so only length is enforced here.
    z.object({ endpoint: z.string().min(5).max(1000) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("push_subscriptions" as never)
      .update({ revoked_at: new Date().toISOString() } as never)
      .eq("endpoint" as never, data.endpoint);
    return { ok: true };
  });
